import { FieldWarning, money } from './inputSanityModel'
import { getSummary } from '../budgetCalculatorModel'
import { calculateStatistics } from '../budgetModel'
import { createWorksheetFromBudgetBuilder, growFieldRoster, withCrewHoursPerWeek } from './budgetBuilderModel'
import { MAX_ENTITIES } from './constants'
import {
  AdminEmployeesFormData,
  CompanyFormData,
  FieldEmployeesFormData,
  HeavyMachineryFormData,
  OwnerFormData,
  SeasonFormData,
  TrailersFormData,
  TrucksFormData
} from './types'

export const ACCURATE_RATE_MIN = 60
export const ACCURATE_RATE_MAX = 200
export const TARGET_RATE = (ACCURATE_RATE_MIN + ACCURATE_RATE_MAX) / 2
export const ZERO_SLIP_FACTOR = 10
export const MAX_CREW_SEARCH = MAX_ENTITIES
const GOAL_SEARCH_STEPS = 16

export type BuilderSnapshot = {
  companyData: CompanyFormData
  seasonData: SeasonFormData
  ownerData: OwnerFormData
  fieldEmployeesData: FieldEmployeesFormData
  adminEmployeesData: AdminEmployeesFormData
  trucksData: TrucksFormData
  trailersData: TrailersFormData
  heavyMachineryData: HeavyMachineryFormData
}

export type CrewCapacity = {
  rate: number
  billableHours: number
  goal: number
  crewCount: number
  goalSupported: number | null
  crewNeeded: number | null
  rateAtGoalSupported: number | null
}

export const withSalesGoal = (snapshot: BuilderSnapshot, salesGoal: number): BuilderSnapshot => ({
  ...snapshot,
  companyData: { ...snapshot.companyData, salesGoal }
})

export const withCrewCount = (snapshot: BuilderSnapshot, count: number): BuilderSnapshot => {
  const employees = growFieldRoster(
    snapshot.fieldEmployeesData.employees,
    count,
    snapshot.seasonData.hoursPerWeek
  )
  return {
    ...snapshot,
    fieldEmployeesData: { employeeCount: count, employees }
  }
}

const moneyRate = (n: number): string => `$${n.toFixed(2)}`

const tidyGoal = (value: number): number => {
  if (value >= 1000000) {
    return Math.floor(value / 100000) * 100000
  }
  if (value >= 100000) {
    return Math.floor(value / 10000) * 10000
  }
  return Math.floor(value / 1000) * 1000
}

type Priced = {
  rate: number
  billableHours: number
}

export const priceSnapshot = (snapshot: BuilderSnapshot): Priced | null => {
  if (snapshot.seasonData.seasonStartDate === null || snapshot.seasonData.seasonEndDate === null) {
    return null
  }
  try {
    const worksheet = createWorksheetFromBudgetBuilder(
      snapshot.companyData,
      snapshot.seasonData,
      snapshot.ownerData,
      snapshot.fieldEmployeesData,
      snapshot.adminEmployeesData,
      snapshot.trucksData,
      snapshot.trailersData,
      snapshot.heavyMachineryData
    )
    const statistics = calculateStatistics(getSummary(worksheet.data, worksheet.metadata), worksheet.data.profit)
    if (!isFinite(statistics.manHourPrice) || !isFinite(statistics.totalBillableHours)) {
      return null
    }
    return { rate: statistics.manHourPrice, billableHours: statistics.totalBillableHours }
  } catch {
    return null
  }
}

export const rateFor = (snapshot: BuilderSnapshot): number => {
  return priceSnapshot(snapshot)?.rate ?? NaN
}

const solveGoal = (snapshot: BuilderSnapshot, goal: number): number | null => {
  let low = 0
  let high = Math.max(goal, 1000000)
  for (let step = 0; step < GOAL_SEARCH_STEPS; step++) {
    const mid = (low + high) / 2
    const rate = rateFor(withSalesGoal(snapshot, mid))
    if (!isFinite(rate)) {
      return null
    }
    if (rate > TARGET_RATE) {
      high = mid
    } else {
      low = mid
    }
  }
  const supported = tidyGoal(low)
  return supported > 0 ? supported : null
}

const solveCrew = (snapshot: BuilderSnapshot): number | null => {
  const current = snapshot.fieldEmployeesData.employees.length
  if (rateFor(withCrewCount(snapshot, MAX_CREW_SEARCH)) > TARGET_RATE) {
    return null
  }
  let low = current + 1
  let high = MAX_CREW_SEARCH
  while (low < high) {
    const mid = Math.floor((low + high) / 2)
    const rate = rateFor(withCrewCount(snapshot, mid))
    if (!isFinite(rate)) {
      return null
    }
    if (rate <= TARGET_RATE) {
      high = mid
    } else {
      low = mid + 1
    }
  }
  return low
}

export const droppingAZeroLands = (snapshot: BuilderSnapshot): boolean => {
  const goal = snapshot.companyData.salesGoal ?? 0
  if (goal <= 0) {
    return false
  }
  const dropped = rateFor(withSalesGoal(snapshot, goal / ZERO_SLIP_FACTOR))
  return isFinite(dropped) && dropped >= ACCURATE_RATE_MIN && dropped <= ACCURATE_RATE_MAX
}

export const checkRevenueScale = (snapshot: BuilderSnapshot): FieldWarning | null => {
  const goal = snapshot.companyData.salesGoal ?? 0
  if (goal <= 0) {
    return null
  }
  const priced = priceSnapshot(snapshot)
  if (priced === null || priced.rate <= ACCURATE_RATE_MAX) {
    return null
  }
  if (!droppingAZeroLands(snapshot)) {
    return null
  }
  const suggested = Math.round(goal / ZERO_SLIP_FACTOR)
  return {
    field: 'revenueScale',
    severity: 'hard',
    text: `Your numbers only work out at about ${moneyRate(priced.rate)} an hour. Double-check you didn't catch an extra zero — ${money(suggested)} puts everything else back in line.`
  }
}

export type CapacityDoor = 'choose' | 'crew' | 'goal' | 'split'

export type DoorDrafts = {
  crew: number | null
  hours: number | null
  goal: number | null
  ownShare: number | null
}

export const previewRateForDoor = (
  snapshot: BuilderSnapshot,
  door: CapacityDoor,
  drafts: DoorDrafts
): number | null => {
  if (door === 'crew') {
    if (drafts.crew === null || drafts.crew <= 0) {
      return null
    }
    const base = drafts.hours !== null && drafts.hours > 0
      ? withCrewHoursPerWeek(snapshot, drafts.hours)
      : snapshot
    return rateFor(withCrewCount(base, drafts.crew))
  }
  if (door === 'goal') {
    if (drafts.goal === null || drafts.goal <= 0) {
      return null
    }
    return rateFor(withSalesGoal(snapshot, drafts.goal))
  }
  if (door === 'split') {
    if (drafts.ownShare === null || drafts.ownShare <= 0) {
      return null
    }
    return rateFor(withSalesGoal(snapshot, drafts.ownShare))
  }
  return null
}

export const evaluateCrewCapacity = (snapshot: BuilderSnapshot): CrewCapacity | null => {
  const goal = snapshot.companyData.salesGoal ?? 0
  if (goal <= 0) {
    return null
  }
  if (droppingAZeroLands(snapshot)) {
    return null
  }
  if (snapshot.fieldEmployeesData.employees.length === 0) {
    return null
  }

  const priced = priceSnapshot(snapshot)
  if (priced === null || priced.rate <= ACCURATE_RATE_MAX || priced.billableHours <= 0) {
    return null
  }

  const goalSupported = solveGoal(snapshot, goal)

  return {
    rate: priced.rate,
    billableHours: priced.billableHours,
    goal,
    crewCount: snapshot.fieldEmployeesData.employees.length,
    goalSupported,
    crewNeeded: solveCrew(snapshot),
    rateAtGoalSupported: goalSupported === null ? null : rateFor(withSalesGoal(snapshot, goalSupported))
  }
}
